import styles from '../../LotteryGameRoomBodyWinners.module.scss';
import cx from 'classnames';

import { MoneyContainer } from '@shared/MoneyContainer';
import { Typography } from '@shared/Typography';
import { FC, memo, useRef, useEffect } from 'react';
import { getOrdinalNumberSuffix } from '@utils/getOrdinalNumberSuffix';
import { Jackpots, UserInfo, WinNumber } from './LotteryGameRoomBodyWinnerBase';
import { LotteryGameRoomBodyWinnerBaseProps } from './lib';
import { LotteryJackpotHistoryWinnerDto } from '@app/store/api/historyApi';
import { LotteryFrontJackpot, SuperTicketType } from '@features/LotteryGame/types';
import { getTicketClassName } from '@features/LotteryGame/helpers/ticketNumberUtils';

interface LotteryGameRoomBodyWinnerProps extends LotteryGameRoomBodyWinnerBaseProps {
  place: number;
  index: number;
  prize: number;
  winNumber: number;
  jackpots?: LotteryFrontJackpot[] | LotteryJackpotHistoryWinnerDto[];
  luckyTicketNumber?: string;
  multiplier?: number;
  placeArrays?: number[];
}

export const LotteryGameRoomBodyWinner: FC<LotteryGameRoomBodyWinnerProps> = memo(
  ({
    user,
    place,
    index,
    prize,
    avatar,
    jackpots,
    winNumber,
    winnerPlayer,
    userDisplayNumber,
    luckyTicketNumber,
    multiplier,
    placeArrays,
  }) => {
    const blockInfoRef = useRef<HTMLDivElement | null>(null);
    const blockUserInfoRef = useRef<HTMLDivElement | null>(null);

    const ticketStyleClass = getTicketClassName(luckyTicketNumber as SuperTicketType);

    useEffect(() => {
      if (blockInfoRef.current && blockUserInfoRef.current) {
        const width = blockInfoRef.current.getBoundingClientRect().width;
        blockUserInfoRef.current.style.maxWidth = `calc(100% - ${width}px)`;
      }
    }, []);

    const showMultiplier = !!(multiplier && placeArrays?.includes(place));

    return (
      <div
        key={index}
        className={cx(styles.lgrbwListItem, {
          [styles.lgrbwListOddItem]: index % 2 === 0,
        })}
      >
        <div ref={blockUserInfoRef} className={styles.lgrbwUserInfo}>
          <div className={styles.lgrbwListItemPlace}>
            <Typography component="h3" variant={'secondary'}>
              {place + getOrdinalNumberSuffix(place)}
            </Typography>
          </div>

          <WinNumber place={place} winNumber={winNumber} />

          <UserInfo
            user={user}
            avatar={avatar}
            userDisplayNumber={userDisplayNumber}
            winnerPlayer={winnerPlayer}
            hasMultiplier={showMultiplier}
          />
        </div>

        <div ref={blockInfoRef} className={styles.lgrbwPrizeInfo}>
          {placeArrays?.includes(place) && luckyTicketNumber && prize > 0 ? (
            <div className={styles.lgrbwLuckyTicketWinner}>
              {multiplier && (
                <Typography
                  component="h3"
                  className={cx(styles.lgrbwLuckyTicketText, styles[ticketStyleClass!])}
                >
                  X {multiplier}
                </Typography>
              )}
              <MoneyContainer
                prize={Number(prize).toFixed(2)}
                variant="dark"
                className={cx(styles.lgrbwLuckyTicketMoney, styles[ticketStyleClass!])}
                luckyTicketNumber={luckyTicketNumber}
              />
            </div>
          ) : (
            prize > 0 && (
              <MoneyContainer
                prize={prize}
                variant={index % 2 === 0 ? 'dark' : 'light'}
              />
            )
          )}

          {prize > 0 && jackpots && jackpots.length > 0 && (
            <div className={styles.lgrbwDivider} />
          )}

          <Jackpots jackpots={jackpots} />
        </div>
      </div>
    );
  },
);
